<template>
  <div>
    <div v-if="carregandoCampos" class="campos-loading">
      Carregando campos...
    </div>
    <div v-else-if="camposAdicionais.length === 0" class="campos-empty">
      Nenhum campo adicional disponível para ticket. Acesse
      <strong>Configurações → Campos Adicionais → Ticket</strong> para criar
      campos.
    </div>
    <div v-else>
      <div class="hint-bar">
        <fa-icon icon="info-circle" class="hint-icon" />
        Segure e arraste pelo ícone <fa-icon icon="grip-vertical" class="hint-grip" /> para definir a ordem de exibição. Salve para confirmar.
      </div>

      <draggable v-model="camposAdicionais" handle=".drag-handle-vinculo" :animation="200" @end="onDragEnd">
        <div
          v-for="campo in camposAdicionais"
          :key="campo.id"
          class="campo-vinculo-row"
        >
          <div class="drag-handle-vinculo">
            <fa-icon icon="grip-vertical" />
          </div>

          <span
            class="campo-ordem-badge"
            :class="{ 'campo-ordem-badge--sem-ordem': !campo.vinculado || campo.ordem == null }"
          >
            {{ campo.vinculado && campo.ordem != null ? '#' + campo.ordem : '—' }}
          </span>

          <div class="campo-vinculo-info">
            <span class="campo-vinculo-label">{{ campo.label }}</span>
            <span class="campo-vinculo-indice">{{ campo.indice }}</span>
          </div>

          <div
            class="campo-vinculo-controls"
            :class="{ 'campo-vinculo-controls--sem-sistemas': !exibirSistemas }"
          >
            <div v-if="exibirSistemas" class="ctrl-sistemas">
              <label class="field-label">Exibir nos sistemas</label>
              <div class="sistemas-chips">
                <label
                  v-for="sis in sistemasDisponiveis"
                  :key="sis.chave"
                  class="sistema-chip"
                  :class="{
                    active: campo.sistemas.includes(sis.chave),
                    'sistema-chip--disabled': !campo.vinculado,
                  }"
                >
                  <input
                    type="checkbox"
                    v-model="campo.sistemas"
                    :value="sis.chave"
                    :disabled="!campo.vinculado"
                  />
                  {{ sis.label }}
                </label>
              </div>
            </div>

            <label
              class="toggle-item"
              :class="{
                active: campo.obrigatorio,
                'toggle-item--disabled': !campo.vinculado,
              }"
            >
              <input
                type="checkbox"
                v-model="campo.obrigatorio"
                :disabled="!campo.vinculado"
              />
              Obrigatório
            </label>

            <label
              class="toggle-item"
              :class="{
                active: campo.visivel_formulario,
                'toggle-item--disabled': !campo.vinculado,
              }"
            >
              <input
                type="checkbox"
                v-model="campo.visivel_formulario"
                :disabled="!campo.vinculado"
              />
              Visível no formulário
            </label>

            <div class="text-dark vincular-col">
              <label class="field-label">Vincular</label>
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  v-model="campo.vinculado"
                  @change="onVinculoChange(campo)"
                />
                <span class="toggle-switch-track"></span>
              </label>
            </div>
          </div>
        </div>
      </draggable>

      <div class="xrow edit-buttons">
        <button
          class="button button-success"
          :disabled="salvandoVinculos"
          @click="salvarVinculos"
        >
          {{ salvandoVinculos ? "Salvando..." : "Salvar vínculos" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { get, put } from "@/helpers/apiRequest";
import draggable from 'vuedraggable';

const parseSistemas = (v) => {
  if (Array.isArray(v)) return v.slice();
  if (typeof v === "string") {
    try {
      return JSON.parse(v);
    } catch {
      return [];
    }
  }
  return [];
};

export default {
  name: "VincularCamposAdicionais",
  components: { draggable },
  props: {
    itemId: { type: Number, default: null },
    endpoint: { type: String, required: true },
    sistemasDisponiveis: { type: Array, default: () => [] },
    exibirSistemas: { type: Boolean, default: true },
  },
  data() {
    return {
      camposAdicionais: [],
      camposSnapshot: [],
      carregandoCampos: false,
      salvandoVinculos: false,
    };
  },
  watch: {
    itemId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.carregar();
        } else {
          this.resetar();
        }
      },
    },
  },
  methods: {
    carregar() {
      this.carregandoCampos = true;
      get(this.endpoint)
        .then((data) => {
          this.camposAdicionais = data.map((campo) => ({
            ...campo,
            sistemas: parseSistemas(campo.sistemas),
          }));
          this.camposSnapshot = JSON.parse(JSON.stringify(this.camposAdicionais));
        })
        .catch(() => {
          this.camposAdicionais = [];
          this.camposSnapshot = [];
        })
        .finally(() => {
          this.carregandoCampos = false;
        });
    },
    resetar() {
      this.camposAdicionais = [];
      this.camposSnapshot = [];
    },
    async _executarSalvamento() {
      const invalidos = this.camposAdicionais.filter(
        (c) => c.vinculado && this.exibirSistemas && c.sistemas.length === 0,
      );
      if (invalidos.length > 0) {
        throw new Error(
          `Selecione pelo menos um sistema para: ${invalidos.map((c) => c.label).join(', ')}`,
        );
      }
      await put(this.endpoint, this.camposAdicionais);
      this.camposSnapshot = JSON.parse(JSON.stringify(this.camposAdicionais));
    },
    async salvarVinculos() {
      this.salvandoVinculos = true;
      try {
        await this._executarSalvamento();
        await this.$swal({
          title: 'Salvo com sucesso',
          icon: 'success',
          showConfirmButton: true,
          showCancelButton: false,
        });
        this.$emit('saved');
      } catch (err) {
        await this.$swal({
          title: 'Falha ao Salvar',
          text: err.message,
          icon: 'error',
          showCancelButton: true,
          cancelButtonText: 'Fechar',
          showConfirmButton: false,
        });
      } finally {
        this.salvandoVinculos = false;
      }
    },
    syncSistemas() {
      if (!this.exibirSistemas) return;
      const chaves = this.sistemasDisponiveis.map((s) => s.chave);
      this.camposAdicionais.forEach((campo) => {
        const validos = campo.sistemas.filter((s) => chaves.includes(s));
        if (validos.length === campo.sistemas.length) return;
        campo.sistemas = validos;
        if (campo.sistemas.length === 0 && campo.vinculado) {
          campo.vinculado = false;
          campo.obrigatorio = false;
        }
      });
      this.camposSnapshot = JSON.parse(JSON.stringify(this.camposAdicionais));
    },
    isDirty() {
      return JSON.stringify(this.camposAdicionais) !== JSON.stringify(this.camposSnapshot);
    },
    discard() {
      this.camposAdicionais = JSON.parse(JSON.stringify(this.camposSnapshot));
    },
    async save() {
      this.salvandoVinculos = true;
      try {
        await this._executarSalvamento();
        return true;
      } finally {
        this.salvandoVinculos = false;
      }
    },
    onDragEnd() {
      this.camposAdicionais.forEach((campo, index) => {
        campo.ordem = index + 1;
      });
    },
    onVinculoChange(campo) {
      if (!campo.vinculado) {
        campo.obrigatorio = false;
        campo.visivel_formulario = true;
        campo.sistemas = [];
      } else if (this.sistemasDisponiveis.length === 1) {
        campo.sistemas = [this.sistemasDisponiveis[0].chave];
      }
      this.recalcularOrdem();
    },
    recalcularOrdem() {
      let ordem = 1;
      this.camposAdicionais.forEach((campo) => {
        campo.ordem = campo.vinculado ? ordem++ : null;
      });
    },
  },
};
</script>

<style scoped>
.field-label {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 3px;
  display: block;
}

.text-dark {
  color: #000 !important;
}

.hint-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #667085;
  background: #f9fafb;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
}

.hint-icon {
  font-size: 13px;
  flex-shrink: 0;
}

.hint-grip {
  font-size: 11px;
  color: #98a2b3;
}

.campos-loading,
.campos-empty {
  padding: 32px 16px;
  text-align: center;
  color: #667085;
  font-size: 14px;
}

.campo-vinculo-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 14px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 8px;
}

.campo-vinculo-row:last-child {
  margin-bottom: 0;
}

.campo-vinculo-info {
  min-width: 200px;
}

.campo-vinculo-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.campo-vinculo-indice {
  display: block;
  font-size: 11px;
  color: #98a2b3;
  font-family: monospace;
}

.campo-vinculo-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;
}

.campo-vinculo-controls--sem-sistemas {
  justify-content: flex-end;
  gap: 40px;
}

.vincular-col {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.vincular-col .field-label {
  color: inherit;
}

.toggle-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #667085;
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: 0;
}

.toggle-item.active {
  border-color: #3a3e81;
  background: #f2f2f8;
  color: #3a3e81;
}

.toggle-item--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.toggle-switch {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;
  margin-bottom: 0;
  white-space: nowrap;
}

.toggle-switch input[type="checkbox"] {
  display: none;
}

.toggle-switch-track {
  position: relative;
  width: 34px;
  height: 18px;
  background: #d0d5dd;
  border-radius: 9px;
  flex-shrink: 0;
  transition: background 0.2s;
}

.toggle-switch-track::after {
  content: "";
  position: absolute;
  width: 12px;
  height: 12px;
  background: #fff;
  border-radius: 50%;
  top: 3px;
  left: 3px;
  transition: transform 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}

.toggle-switch input:checked + .toggle-switch-track {
  background: #3a3e81;
}

.toggle-switch input:checked + .toggle-switch-track::after {
  transform: translateX(16px);
}

.toggle-switch-text {
  font-size: 12px;
  font-weight: 600;
  color: #344054;
}

.sistemas-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.sistema-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border: 1px solid #e4e7eb;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #667085;
  cursor: pointer;
  transition: all 0.15s;
  margin-bottom: 0;
}

.sistema-chip input[type="checkbox"] {
  display: none;
}

.sistema-chip.active {
  border-color: #3a3e81;
  background: #eceaf3;
  color: #3a3e81;
}

.sistema-chip--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.drag-handle-vinculo {
  cursor: grab;
  color: #98a2b3;
  padding: 2px 6px;
  font-size: 14px;
  flex-shrink: 0;
}

.drag-handle-vinculo:active {
  cursor: grabbing;
}

.campo-ordem-badge {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  background: #e9ecef;
  padding: 1px 6px;
  border-radius: 4px;
  flex-shrink: 0;
  min-width: 28px;
  text-align: center;
}

.campo-ordem-badge--sem-ordem {
  color: #c1c7d0;
  background: #f4f5f7;
}
</style>
